/**
 * 训练模型卡片组件
 * v10.2 新增：显示单个训练模型的信息和操作
 */

import { useState, useRef } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Input } from '@/shared/components/ui/input'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/ui/collapsible'
import {
  Play,
  Pause,
  ChevronDown,
  ChevronUp,
  Star,
  Pencil,
  Trash2,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import type { VoiceTrainingModel } from '@/modules/identity/types'

interface VoiceModelCardProps {
  model: VoiceTrainingModel
  cosyvoiceStatus?: 'OK' | 'DEPLOYING' | 'UNDEPLOYED'
  onPreview: (modelId: string, text: string) => Promise<string>
  onActivate: (modelId: string) => Promise<void>
  onRename: (modelId: string) => void
  onDelete: (modelId: string) => void
  isActivating?: boolean
  isPreviewing?: boolean
  previewUrl?: string | null
}

/**
 * 格式化训练时间
 */
function formatTrainedTime(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * CosyVoice 状态配置
 */
const COSYVOICE_STATUS_CONFIG = {
  OK: { label: '已就绪', color: 'text-emerald-600', bgColor: 'bg-emerald-50', icon: CheckCircle },
  DEPLOYING: { label: '审核中', color: 'text-amber-600', bgColor: 'bg-amber-50', icon: Clock },
  UNDEPLOYED: { label: '审核失败', color: 'text-red-600', bgColor: 'bg-red-50', icon: XCircle },
}

export function VoiceModelCard({
  model,
  cosyvoiceStatus = 'OK',
  onPreview,
  onActivate,
  onRename,
  onDelete,
  isActivating = false,
  isPreviewing = false,
  previewUrl,
}: VoiceModelCardProps) {
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewText, setPreviewText] = useState('你好，很高兴见到你！')
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)

  const statusConfig = COSYVOICE_STATUS_CONFIG[cosyvoiceStatus]
  const StatusIcon = statusConfig.icon

  const handlePreview = async () => {
    if (!previewText.trim()) return

    setIsLoadingPreview(true)
    try {
      const url = await onPreview(model.model_id, previewText)
      setLocalPreviewUrl(url)
      
      // 自动播放 - 等待音频加载完成后再播放，避免 AbortError
      if (audioRef.current) {
        const audio = audioRef.current
        audio.src = url
        audio.load()
        
        // 使用一次性事件监听器
        const handleCanPlay = () => {
          audio.play().catch((err) => {
            // 忽略 AbortError（用户快速点击导致的中断）
            if (err.name !== 'AbortError') {
              console.error('Play failed:', err)
            }
          })
          setIsPlaying(true)
          audio.removeEventListener('canplaythrough', handleCanPlay)
        }
        audio.addEventListener('canplaythrough', handleCanPlay)
      }
    } catch (error) {
      console.error('Preview failed:', error)
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const togglePlay = () => {
    if (!audioRef.current) return
    
    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      audioRef.current.play()
      setIsPlaying(true)
    }
  }

  return (
    <div
      className={cn(
        'border rounded-lg p-3 transition-all',
        model.is_active
          ? 'border-violet-300 bg-violet-50/50'
          : 'border-slate-200 hover:border-slate-300'
      )}
    >
      <audio
        ref={audioRef}
        src={localPreviewUrl || previewUrl || undefined}
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
      />

      {/* 顶部：名称 + 状态 */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm truncate" title={model.name}>
              {model.name}
            </span>
            {model.is_active && (
              <Badge variant="secondary" className="text-xs bg-violet-100 text-violet-700 flex-shrink-0">
                <Star className="h-3 w-3 mr-1 fill-current" />
                使用中
              </Badge>
            )}
          </div>
          <p
            className="text-xs text-slate-400 font-mono truncate"
            title={model.model_id}
          >
            {model.model_id.length > 30
              ? `${model.model_id.slice(0, 30)}...`
              : model.model_id}
          </p>
        </div>
        {/* CosyVoice 状态 */}
        <Badge
          variant="secondary"
          className={cn('text-xs flex-shrink-0 ml-2', statusConfig.bgColor, statusConfig.color)}
        >
          <StatusIcon className="h-3 w-3 mr-1" />
          {statusConfig.label}
        </Badge>
      </div>

      {/* 训练时间 */}
      <p className="text-xs text-slate-500 mb-3">
        {formatTrainedTime(model.trained_at)}
      </p>

      {/* 试听折叠区 */}
      <Collapsible open={previewOpen} onOpenChange={setPreviewOpen}>
        <div className="flex items-center gap-2 flex-wrap">
          <CollapsibleTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 text-xs">
              {previewOpen ? (
                <ChevronUp className="h-3 w-3 mr-1" />
              ) : (
                <ChevronDown className="h-3 w-3 mr-1" />
              )}
              试听
            </Button>
          </CollapsibleTrigger>

          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => onRename(model.model_id)}
          >
            <Pencil className="h-3 w-3 mr-1" />
            重命名
          </Button>

          {!model.is_active && cosyvoiceStatus === 'OK' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-violet-600 hover:text-violet-700 hover:bg-violet-50"
              onClick={() => onActivate(model.model_id)}
              disabled={isActivating}
            >
              {isActivating ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Star className="h-3 w-3 mr-1" />
              )}
              使用
            </Button>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={() => onDelete(model.model_id)}
          >
            <Trash2 className="h-3 w-3 mr-1" />
            删除
          </Button>
        </div>

        <CollapsibleContent className="mt-3">
          <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
            <div className="flex gap-2">
              <Input
                value={previewText}
                onChange={(e) => setPreviewText(e.target.value)}
                placeholder="输入试听文本..."
                className="h-8 text-sm flex-1"
                maxLength={200}
              />
              <Button
                size="sm"
                className="h-8"
                onClick={handlePreview}
                disabled={isLoadingPreview || isPreviewing || !previewText.trim()}
              >
                {isLoadingPreview || isPreviewing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* 音频播放器 */}
            {(localPreviewUrl || previewUrl) && (
              <div className="flex items-center gap-2 p-2 bg-white rounded border">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={togglePlay}
                >
                  {isPlaying ? (
                    <Pause className="h-4 w-4" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                </Button>
                <div className="flex-1 h-1 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full w-0 bg-violet-500 rounded-full" />
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

/**
 * 训练模型卡片骨架屏
 */
export function VoiceModelCardSkeleton() {
  return (
    <div className="border rounded-lg p-3 animate-pulse">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="h-4 w-24 bg-slate-200 rounded mb-1" />
          <div className="h-3 w-40 bg-slate-200 rounded" />
        </div>
        <div className="h-5 w-16 bg-slate-200 rounded" />
      </div>
      <div className="h-3 w-20 bg-slate-200 rounded mb-3" />
      <div className="h-7 w-full bg-slate-200 rounded" />
    </div>
  )
}
